import {
  pushSuccessNotification,
  useLocale,
  useTranslation
} from '@plandi/common'
import { components } from '@plandi/common/src/generated'
import { AxiosError } from 'axios'
import { useRouter } from 'next/router'
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query'
import { MY_PROFILE_SPECIALIST_KEY_NEW } from 'src/features/profile-specialist/common'

import { useThunkDispatch } from 'src/store'
import { portfolioApi } from './portfolio.api'
import {
  PORTFOLIO_PROJECT_KEY,
  PORTFOLIO_PROJECT_LIST_KEY
} from './portfolio.constants'
import { PortfolioSaveProjectForm } from './portfolio.types'
import { useUploadImageProjectPortfolio } from './use-upload-image-project-portfolio'

export const useMutationLikeProject = (
  options?: UseMutationOptions<{ likes?: number }, AxiosError, number>
) => {
  const dispatch = useThunkDispatch()
  const queryClient = useQueryClient()
  const router = useRouter()
  const { data: translate } = useTranslation()
  const locale = useLocale()

  const slug = router.query.slug as string | undefined
  const profileId = router.query.profileId
    ? Number(router.query.profileId)
    : undefined

  const queryParams = router.query.sort ? { sort: router.query.sort } : {}

  const mutation = useMutation(
    async (projectId) => portfolioApi.addLikeProject(projectId, locale),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        dispatch(
          pushSuccessNotification(
            translate?.['like-added-successfully'] ?? 'Лайк успешно добавлен'
          )
        )

        queryClient.setQueryData<
          components['schemas']['PortfolioProjectResource'][] | undefined
        >(
          [PORTFOLIO_PROJECT_LIST_KEY, profileId, queryParams, locale],
          (projects) =>
            projects?.map((project) =>
              project.id === variables
                ? { ...project, isLiked: !project.isLiked, likes: data.likes }
                : project
            )
        )

        queryClient.setQueryData<
          components['schemas']['ProfileProjectResource']
        >([PORTFOLIO_PROJECT_KEY, slug, locale], (project) => ({
          ...project,
          isLiked: !project?.isLiked,
          likes: data.likes
        }))

        queryClient.invalidateQueries([PORTFOLIO_PROJECT_KEY, slug, locale])
        queryClient.invalidateQueries([
          PORTFOLIO_PROJECT_LIST_KEY,
          profileId,
          queryParams,
          locale
        ])

        options?.onSuccess?.(data, variables, context)
      }
    }
  )

  return mutation
}

export const useMutationDislikeProject = (
  options?: UseMutationOptions<{ likes?: number }, AxiosError, number>
) => {
  const dispatch = useThunkDispatch()
  const queryClient = useQueryClient()
  const router = useRouter()
  const { data: translate } = useTranslation()
  const locale = useLocale()

  const slug = router.query.slug as string | undefined
  const profileId = router.query.profileId
    ? Number(router.query.profileId)
    : undefined
  const queryParams = router.query.sort ? { sort: router.query.sort } : {}

  const mutation = useMutation(
    async (projectId) => portfolioApi.dislikeProject(projectId, locale),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        dispatch(
          pushSuccessNotification(
            translate?.['like-successfully-deleted'] ?? 'Лайк успешно удален'
          )
        )

        queryClient.setQueryData<
          components['schemas']['PortfolioProjectResource'][] | undefined
        >(
          [PORTFOLIO_PROJECT_LIST_KEY, profileId, queryParams, locale],
          (projects) =>
            projects?.map((project) =>
              project.id === variables
                ? { ...project, isLiked: !project.isLiked, likes: data.likes }
                : project
            )
        )

        queryClient.setQueryData<
          components['schemas']['ProfileProjectResource']
        >([PORTFOLIO_PROJECT_KEY, slug, locale], (project) => ({
          ...project,
          isLiked: !project?.isLiked,
          likes: data.likes
        }))

        queryClient.invalidateQueries([PORTFOLIO_PROJECT_KEY, slug, locale])
        queryClient.invalidateQueries([
          PORTFOLIO_PROJECT_LIST_KEY,
          profileId,
          queryParams,
          locale
        ])

        options?.onSuccess?.(data, variables, context)
      }
    }
  )

  return mutation
}

export const useMutationDeleteProject = (
  id?: number,
  options?: UseMutationOptions<unknown, AxiosError, number>
) => {
  const dispatch = useThunkDispatch()
  const queryClient = useQueryClient()
  const router = useRouter()
  const { data: translate } = useTranslation()
  const locale = useLocale()

  const slug = router.query.slug as string | undefined
  const profileId = id
    ? Number(id)
    : router.query.profileId
    ? Number(router.query.profileId)
    : undefined

  const queryParams = router.query.sort ? { sort: router.query.sort } : {}

  const mutation = useMutation(
    async (projectId) => portfolioApi.deleteProject(projectId, locale),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        dispatch(
          pushSuccessNotification(
            translate?.['project-deleted'] ?? 'Проект успешно удален'
          )
        )

        queryClient.invalidateQueries([MY_PROFILE_SPECIALIST_KEY_NEW, locale])
        queryClient.invalidateQueries([
          PORTFOLIO_PROJECT_LIST_KEY,
          profileId,
          queryParams,
          locale
        ])

        queryClient.setQueryData<
          components['schemas']['PortfolioProjectResource'][] | undefined
        >(
          [PORTFOLIO_PROJECT_LIST_KEY, profileId, queryParams, locale],
          (projects) => projects?.filter((project) => project.id !== variables)
        )

        if (slug) {
          queryClient.removeQueries([PORTFOLIO_PROJECT_KEY, slug, locale])
        }

        options?.onSuccess?.(data, variables, context)
      }
    }
  )

  return mutation
}

export const useMutationAddProjectToPortfolio = (
  id?: number,
  options?: UseMutationOptions<
    { slug?: string },
    AxiosError,
    PortfolioSaveProjectForm
  >
) => {
  const dispatch = useThunkDispatch()
  const queryClient = useQueryClient()
  const router = useRouter()
  const { data: translate } = useTranslation()
  const locale = useLocale()

  const profileId = id
    ? Number(id)
    : router.query.profileId
    ? Number(router.query.profileId)
    : undefined

  const queryParams = router.query.sort ? { sort: router.query.sort } : {}

  const getImagesProjectPortfolio = useUploadImageProjectPortfolio()

  const mutation = useMutation(
    async (requestBody) => {
      if (!profileId) {
        throw new Error('Отсутствует идентификатор пользователя')
      }

      const images = await getImagesProjectPortfolio(requestBody.images)

      return await portfolioApi.addProjectToPortfolio(
        profileId,
        {
          ...requestBody,
          images
        },
        locale
      )
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        dispatch(
          pushSuccessNotification(
            translate?.['project-successfully-created'] ??
              'Проект успешно создан'
          )
        )

        queryClient.invalidateQueries([
          PORTFOLIO_PROJECT_LIST_KEY,
          profileId,
          queryParams,
          locale
        ])

        options?.onSuccess?.(data, variables, context)
      }
    }
  )

  return mutation
}

export const useMutationUpdateProjectFromPortfolio = (
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    { project: PortfolioSaveProjectForm; projectId: number },
    number
  >
) => {
  const dispatch = useThunkDispatch()
  const queryClient = useQueryClient()
  const getImagesProjectPortfolio = useUploadImageProjectPortfolio()
  const router = useRouter()
  const { data: translate } = useTranslation()
  const locale = useLocale()

  const slug = router.query.slug as string | undefined

  const mutation = useMutation(
    async (requestBody) => {
      const images = await getImagesProjectPortfolio(requestBody.project.images)

      return await portfolioApi.updateProjectFromPortfolio(
        requestBody.projectId,
        {
          ...requestBody.project,
          images
        },
        locale
      )
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        dispatch(
          pushSuccessNotification(
            translate?.['project-successfully-modified'] ??
              'Проект успешно изменен'
          )
        )

        queryClient.invalidateQueries([PORTFOLIO_PROJECT_KEY, slug, locale])

        options?.onSuccess?.(data, variables, context)
      }
    }
  )

  return mutation
}
